import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import { Brain, Search, Loader2, RefreshCw, X, Info, Share2 } from "lucide-react";
import api from "@/lib/api";

const TIPO_LABEL = {
  facto: "facto",
  decisao: "decisão",
  preferencia: "preferência",
  tarefa: "tarefa",
  aprendizagem: "aprendizagem",
};

export default function CerebroPage() {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const s = useRef({
    yaw: 0.6,
    pitch: -0.35,
    zoom: 1,
    dragging: false,
    lastX: 0,
    lastY: 0,
    moved: false,
    points: [],
    ligacoes: [],
    links: true,
    query: "",
    selectedId: null,
    auto: true,
    dpr: 1,
    w: 0,
    h: 0,
    proj: [],
  });

  const [points, setPoints] = useState([]);
  const [showLinks, setShowLinks] = useState(true);
  const [entidades, setEntidades] = useState([]);
  const [entidadeFiltro, setEntidadeFiltro] = useState("todas");
  const [memorias, setMemorias] = useState([]);
  const [motivo, setMotivo] = useState(undefined);
  const [modo, setModo] = useState(null);
  const [meta, setMeta] = useState({ total_factos: 0, com_embedding: 0 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");
  const [reindexing, setReindexing] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const url = entidadeFiltro === "todas" ? "/cerebro" : `/cerebro?entidade=${entidadeFiltro}`;
      const { data } = await api.get(url);
      setPoints(data.pontos || []);
      setMemorias(data.memorias || []);
      setMotivo(data.motivo);
      setModo(data.modo);
      setMeta({ total_factos: data.total_factos, com_embedding: data.com_embedding });
      s.current.points = data.pontos || [];
      s.current.ligacoes = data.ligacoes || [];
      s.current.selectedId = null;
      setSelected(null);
    } catch (e) {
      toast.error("Não foi possível carregar o cérebro");
    } finally {
      setLoading(false);
    }
  }, [entidadeFiltro]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    api.get("/entidades").then((r) => setEntidades(r.data)).catch(() => {});
  }, []);

  const reindexar = async () => {
    setReindexing(true);
    try {
      const { data } = await api.post("/cerebro/reindexar");
      if (data.embeddings_gerados > 0) {
        toast.success(`${data.embeddings_gerados} embeddings gerados`);
      } else {
        toast.info("Sem embeddings novos (adiciona a OPENAI_API_KEY ao backend/.env).");
      }
      await carregar();
    } catch (e) {
      toast.error("Falha ao reindexar");
    } finally {
      setReindexing(false);
    }
  };

  // ---- render loop (projeção 3D manual em canvas 2D) ----
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;

    const resize = () => {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      s.current.dpr = dpr;
      s.current.w = wrap.clientWidth;
      s.current.h = wrap.clientHeight;
      canvas.width = s.current.w * dpr;
      canvas.height = s.current.h * dpr;
      canvas.style.width = s.current.w + "px";
      canvas.style.height = s.current.h + "px";
    };
    resize();
    const ro = new ResizeObserver(resize);
    if (wrapRef.current) ro.observe(wrapRef.current);

    const project = (p, cx, cy, scale) => {
      const cy1 = Math.cos(s.current.yaw), sy = Math.sin(s.current.yaw);
      let x = p.x * cy1 - p.z * sy;
      let z = p.x * sy + p.z * cy1;
      const y = p.y;
      const cp = Math.cos(s.current.pitch), sp = Math.sin(s.current.pitch);
      const y2 = y * cp - z * sp;
      const z2 = y * sp + z * cp;
      const f = 3 / (3 + z2); // leve perspetiva
      return { sx: cx + x * scale * f, sy: cy + y2 * scale * f, depth: z2, f };
    };

    const draw = () => {
      const { dpr, w, h } = s.current;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      // fundo escuro com vinheta
      const g = ctx.createRadialGradient(w / 2, h / 2, 40, w / 2, h / 2, Math.max(w, h) * 0.75);
      g.addColorStop(0, "#0b1220");
      g.addColorStop(1, "#05070d");
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);

      if (s.current.auto && !s.current.dragging) s.current.yaw += 0.0022;

      const cx = w / 2, cy = h / 2;
      const scale = Math.min(w, h) * 0.34 * s.current.zoom;
      const q = s.current.query.trim().toLowerCase();

      const matchOf = (p) =>
        !q || p.texto.toLowerCase().includes(q) || (p.memoria_titulo || "").toLowerCase().includes(q);

      // projeção em ORDEM ORIGINAL (para as ligações usarem os índices certos)
      const projByIdx = s.current.points.map((p) => ({ p, ...project(p, cx, cy, scale), match: matchOf(p) }));

      // 1) desenhar as ligações (a teia) por baixo dos pontos
      if (s.current.links && s.current.ligacoes.length) {
        for (const [a, b] of s.current.ligacoes) {
          const pa = projByIdx[a], pb = projByIdx[b];
          if (!pa || !pb) continue;
          const hit = pa.match && pb.match;
          if (q && !(pa.match || pb.match)) continue;
          ctx.beginPath();
          ctx.moveTo(pa.sx, pa.sy);
          ctx.lineTo(pb.sx, pb.sy);
          ctx.strokeStyle = pa.p.cor;
          ctx.globalAlpha = q ? (hit ? 0.5 : 0.05) : 0.14 * (0.5 + Math.min(pa.f, pb.f) * 0.5);
          ctx.lineWidth = 1;
          ctx.stroke();
        }
      }

      // 2) desenhar os pontos por cima, ordenados por profundidade
      const proj = projByIdx.slice().sort((a, b) => b.depth - a.depth);
      s.current.proj = proj;

      for (const item of proj) {
        const { p, sx, sy, f, match } = item;
        const sel = s.current.selectedId === p.id;
        const r = (sel ? 6 : 3.6) * f * (match ? 1 : 0.7);
        ctx.beginPath();
        ctx.arc(sx, sy, Math.max(1, r), 0, Math.PI * 2);
        ctx.globalAlpha = q ? (match ? 1 : 0.08) : 0.85 * (0.5 + f * 0.5);
        ctx.fillStyle = p.cor;
        ctx.shadowColor = p.cor;
        ctx.shadowBlur = match ? (sel ? 18 : 8) * f : 0;
        ctx.fill();
        if (sel) {
          ctx.shadowBlur = 0;
          ctx.globalAlpha = 1;
          ctx.lineWidth = 1.5;
          ctx.strokeStyle = "#ffffff";
          ctx.beginPath();
          ctx.arc(sx, sy, r + 4, 0, Math.PI * 2);
          ctx.stroke();
        }
      }
      ctx.globalAlpha = 1;
      ctx.shadowBlur = 0;
      raf = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, []);

  // ---- interação ----
  const onDown = (e) => {
    const st = s.current;
    st.dragging = true;
    st.moved = false;
    st.auto = false;
    const pt = "touches" in e ? e.touches[0] : e;
    st.lastX = pt.clientX;
    st.lastY = pt.clientY;
  };
  const onMove = (e) => {
    const st = s.current;
    if (!st.dragging) return;
    const pt = "touches" in e ? e.touches[0] : e;
    const dx = pt.clientX - st.lastX;
    const dy = pt.clientY - st.lastY;
    if (Math.abs(dx) + Math.abs(dy) > 3) st.moved = true;
    st.yaw += dx * 0.006;
    st.pitch += dy * 0.006;
    st.pitch = Math.max(-1.4, Math.min(1.4, st.pitch));
    st.lastX = pt.clientX;
    st.lastY = pt.clientY;
  };
  const onUp = (e) => {
    const st = s.current;
    st.dragging = false;
    if (!st.moved) {
      // clique → hit test
      const rect = canvasRef.current.getBoundingClientRect();
      const pt = "changedTouches" in e ? e.changedTouches[0] : e;
      const mx = pt.clientX - rect.left;
      const my = pt.clientY - rect.top;
      let best = null, bestD = 14;
      for (const item of st.proj) {
        const d = Math.hypot(item.sx - mx, item.sy - my);
        if (d < bestD) { bestD = d; best = item.p; }
      }
      st.selectedId = best ? best.id : null;
      setSelected(best || null);
    }
  };
  const onWheel = (e) => {
    e.preventDefault();
    const st = s.current;
    st.zoom = Math.max(0.4, Math.min(4, st.zoom * (e.deltaY > 0 ? 0.92 : 1.08)));
  };

  const onSearch = (v) => {
    setQuery(v);
    s.current.query = v;
  };

  const semDados = !loading && (motivo === "vazio" || points.length === 0);

  return (
    <div className="h-full flex flex-col bg-[#05070d]" data-testid="cerebro-page">
      <header className="h-14 border-b border-slate-800/80 flex items-center justify-between px-4 sm:px-6 shrink-0 bg-[#0a0f1a]">
        <div className="flex items-center gap-2">
          <Brain size={18} className="text-emerald-400" />
          <h1 className="font-display font-bold text-base sm:text-lg">Cérebro</h1>
          {!loading && (
            <span className="hidden sm:inline text-xs text-slate-500 font-mono ml-2">
              {meta.total_factos} factos mapeados
              {modo && modo !== "semantico" ? " · modo aproximado" : modo === "semantico" ? " · semântico" : ""}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative hidden sm:block">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              data-testid="cerebro-search-input"
              value={query}
              onChange={(e) => onSearch(e.target.value)}
              placeholder="acender factos…"
              className="w-52 bg-slate-900/70 border border-slate-800 rounded-lg pl-8 pr-3 py-1.5 text-sm outline-none focus:border-emerald-500/50"
            />
          </div>
          <select
            data-testid="cerebro-entity-filter"
            value={entidadeFiltro}
            onChange={(e) => setEntidadeFiltro(e.target.value)}
            className="hidden sm:block bg-slate-900/70 border border-slate-800 rounded-lg px-2 py-1.5 text-sm outline-none focus:border-emerald-500/50 max-w-[160px]"
          >
            <option value="todas">Todas as entidades</option>
            {entidades.map((e) => (
              <option key={e.id} value={e.chave}>{e.nome}</option>
            ))}
          </select>
          <button
            data-testid="cerebro-links-toggle"
            onClick={() => { const nv = !showLinks; setShowLinks(nv); s.current.links = nv; }}
            title="Mostrar/ocultar ligações"
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-colors ${
              showLinks ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" : "bg-slate-800/70 border-slate-700 text-slate-300 hover:bg-slate-700/70"
            }`}
          >
            <Share2 size={13} /> Ligações
          </button>
          <button
            data-testid="cerebro-reindex-button"
            onClick={reindexar}
            disabled={reindexing}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-800/70 hover:bg-slate-700/70 border border-slate-700 text-slate-200 text-xs font-semibold transition-colors disabled:opacity-50"
          >
            {reindexing ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Reindexar
          </button>
        </div>
      </header>

      <div className="flex-1 relative overflow-hidden" ref={wrapRef}>
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center text-slate-400 z-10">
            <Loader2 className="animate-spin" size={22} />
          </div>
        )}

        <canvas
          ref={canvasRef}
          data-testid="cerebro-canvas"
          className="absolute inset-0 touch-none cursor-grab active:cursor-grabbing"
          onMouseDown={onDown}
          onMouseMove={onMove}
          onMouseUp={onUp}
          onMouseLeave={() => (s.current.dragging = false)}
          onWheel={onWheel}
          onTouchStart={onDown}
          onTouchMove={onMove}
          onTouchEnd={onUp}
        />

        {/* Estado vazio / sem embeddings */}
        {semDados && (
          <div className="absolute inset-0 flex items-center justify-center p-6 z-20" data-testid="cerebro-empty">
            <div className="max-w-md text-center bg-[#0a0f1a]/90 border border-slate-800 rounded-2xl p-7 backdrop-blur">
              <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 border border-emerald-500/25 flex items-center justify-center mx-auto mb-4">
                <Brain size={26} className="text-emerald-400" />
              </div>
              <h2 className="font-display text-xl font-bold mb-2">O cérebro está vazio</h2>
              <p className="text-sm text-slate-400 leading-relaxed mb-4">
                Ainda não há factos na memória. Conversa, guarda uma conversa, ou importa fontes
                (vídeos, PDFs, ChatGPT) na página <strong className="text-slate-200">Fontes</strong> — cada facto
                aparece aqui como um ponto.
              </p>
              <p className="text-xs text-slate-500 leading-relaxed mb-5">
                Quando ativares os embeddings (OPENAI_API_KEY no backend/.env), as posições passam a refletir o
                <strong> significado</strong>. Sem embeddings, mostro tudo em modo aproximado, agrupado por memória.
              </p>
              <button
                data-testid="cerebro-empty-reindex"
                onClick={reindexar}
                disabled={reindexing}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm transition-colors disabled:opacity-50"
              >
                {reindexing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Reindexar embeddings
              </button>
            </div>
          </div>
        )}

        {/* Legenda de memórias (cor por memória) */}
        {!semDados && memorias.length > 0 && (
          <div
            className="absolute top-3 left-3 max-w-[220px] max-h-[45%] overflow-auto bg-[#0a0f1a]/80 border border-slate-800 rounded-xl p-3 backdrop-blur text-xs space-y-1.5 z-10"
            data-testid="cerebro-legend"
          >
            <div className="text-slate-500 font-mono mb-1 flex items-center gap-1"><Info size={11} /> cor por memória</div>
            {memorias.map((m) => (
              <div key={m.id} className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: m.cor, boxShadow: `0 0 6px ${m.cor}` }} />
                <span className="text-slate-300 truncate">{m.titulo}</span>
              </div>
            ))}
          </div>
        )}

        {/* Aviso de modo aproximado */}
        {!semDados && modo && modo !== "semantico" && (
          <div
            className="absolute top-3 right-3 max-w-[260px] bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 backdrop-blur text-xs z-10"
            data-testid="cerebro-modo-aproximado"
          >
            <div className="text-amber-300 font-semibold mb-1 flex items-center gap-1"><Info size={12} /> modo aproximado</div>
            <p className="text-amber-200/70 leading-relaxed">
              Estão todos os factos mapeados e agrupados por memória, mas as posições ainda não refletem o
              significado. Ativa os embeddings e carrega em <strong>Reindexar</strong> para o mapa semântico real.
            </p>
          </div>
        )}

        {/* Dica de interação */}
        {!semDados && !selected && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 text-[11px] text-slate-500 font-mono bg-[#0a0f1a]/70 border border-slate-800 rounded-full px-3 py-1 z-10">
            arrastar roda · roda do rato aproxima · clicar num ponto abre o facto
          </div>
        )}

        {/* Painel do facto selecionado */}
        {selected && (
          <div
            className="absolute bottom-4 right-4 left-4 sm:left-auto sm:w-80 bg-[#0a0f1a]/95 border border-slate-700 rounded-xl p-4 backdrop-blur shadow-2xl z-20"
            data-testid="cerebro-selected-panel"
          >
            <button
              onClick={() => { setSelected(null); s.current.selectedId = null; }}
              className="absolute top-3 right-3 text-slate-500 hover:text-slate-200"
              data-testid="cerebro-close-panel"
            >
              <X size={16} />
            </button>
            <div className="flex items-center gap-2 mb-2">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: selected.cor, boxShadow: `0 0 6px ${selected.cor}` }} />
              <span className="text-[11px] font-mono uppercase tracking-wide text-slate-400">
                {TIPO_LABEL[selected.tipo] || selected.tipo}
              </span>
            </div>
            <p className="text-sm text-slate-100 leading-relaxed mb-3">{selected.texto}</p>
            <div className="text-xs text-slate-500 border-t border-slate-800 pt-2">
              memória: <span className="text-slate-300">{selected.memoria_titulo || "—"}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
